// faqData.js
// Content for the public-facing Diagnostic Intelligence FAQ (120 Q&A).
//
// Editing rules for this file:
//  - Keep every question ID unique and stable. IDs are used for deep links (#SCI-05).
//  - Do not add performance figures, architecture parameters, or implementation
//    details here. Every specific claim needs a traceable source before it is
//    published.
//  - Answers marked "PATENT-REVIEWED" were deliberately generalized. Do not
//    re-introduce specifics without patent counsel sign-off.

export const faqMeta = {
  titleLead: "Frequently asked",
  titleAccent: "questions",
  edition: "Public-Facing Product & Platform Edition",
  date: "September 2026",
  about: [
    "This FAQ is intentionally written for external readers. It explains what Diagnostic Intelligence is designed to enable without exposing proprietary implementation details, internal architecture mechanisms, or unverified performance figures.",
    "DI is being developed as an extensible multimodal intelligence platform for life sciences, healthcare, and MedTech. The initial focus is pulmonary tuberculosis and translational research, with a broader vision for disease characterization, stratification, biomarker research, and precision-medicine applications.",
  ],
  audiences: [
    "Investors",
    "Hospitals / Health Systems",
    "Doctors / Clinicians",
    "Scientists",
    "AI Researchers",
    "Pharma, Biotech & Therapeutics",
  ],
};

export const evidencePrinciple = {
  title: "A simple evidence principle",
  rows: [
    {
      term: "Existing data",
      text: "DI is designed to make use of data that organizations already collect, where appropriate.",
    },
    {
      term: "Paired / linked data",
      text: "Paired or patient-linked multimodal data can be important for particular development, benchmarking, and validation studies.",
    },
    {
      term: "Unpaired data",
      text: "Unpaired data can still contribute to broader research and population-level learning where scientifically appropriate.",
    },
    {
      term: "Clinical validation",
      text: "Clinical validation is tied to the specific clinical claim and intended use; research development and clinical validation are distinct activities.",
    },
  ],
};

export const readingNote = {
  title: "How to read the answers",
  text: "The answers use a product-oriented, forward-looking style: capabilities are described as part of the platform direction and intended product behavior, while specific performance, clinical, and regulatory conclusions remain dependent on the relevant evidence and use case. The aim is to show what the platform is built to achieve without turning future capability into an unsupported present claim.",
};

export const faqParts = [
  // ---------------------------------------------------------------------------
  {
    id: "investors",
    short: "Investors",
    title: "Investors",
    description:
      "Business value, market use, scalability, evidence, IP direction, and the path from platform capability to commercial engagement.",
    items: [
      {
        id: "INV-01",
        q: "What problem does DI solve?",
        a: "DI brings together fragmented disease information so it can be understood as a connected whole. The platform is designed to turn diverse clinical and biological evidence into useful disease insights for research, characterization, stratification, biomarker work, and precision medicine.",
      },
      {
        id: "INV-02",
        q: "Why does it require multimodal AI?",
        a: "Disease is expressed through multiple forms of evidence. DI is designed to connect those complementary signals so that relationships across data types can contribute to a more complete understanding of disease.",
      },
      {
        id: "INV-03",
        q: "Why can't existing unimodal systems solve this?",
        a: "Unimodal systems are valuable but focus on one evidence type at a time. DI is designed to complement them by connecting information across modalities rather than simply combining separate predictions.",
      },
      {
        id: "INV-04",
        q: "What is the initial commercial use case?",
        a: "The initial focus is translational and precision-medicine research, beginning with pulmonary tuberculosis. The platform is intended to support disease characterization, patient stratification, biomarker research, and related scientific workflows.",
      },
      {
        id: "INV-05",
        q: "Who pays for DI?",
        a: "Potential customers include hospitals and health systems, academic and research institutions, pharmaceutical and biotech organizations, diagnostics partners, and research consortia. Engagement can be tailored to the scope and maturity of the collaboration.",
      },
      {
        id: "INV-06",
        q: "What is the economic value to the customer?",
        a: "Potential value comes from making better use of existing multimodal information, accelerating research, supporting biomarker and stratification work, and creating a common platform for translational questions.",
      },
      {
        id: "INV-07",
        q: "How does DI reuse existing healthcare data?",
        a: "DI is designed to make use of data that organizations already collect, where appropriate. Depending on the question, linked or paired multimodal datasets may be important for development, benchmarking, and validation, and new prospective data collection may be appropriate for specific studies.",
      },
      {
        id: "INV-08",
        q: "What are the deployment requirements?",
        a: "DI is designed for flexible deployment across research and enterprise environments, including cloud, on-premises, and hybrid settings. The deployment model can evolve with the organization's data, security, and scale requirements.",
      },
      {
        id: "INV-09",
        q: "How does DI handle missing modalities?",
        a: "DI is designed to work with the modalities that are available rather than requiring every data type for every use case. Missing information can be recognized and communicated so that it is not automatically interpreted as evidence against a finding.",
      },
      {
        id: "INV-10",
        q: "What is implemented today?",
        a: "The platform foundation and core multimodal capabilities are being developed around clinical, imaging, pathology, and acoustic evidence. Additional capabilities are being developed as the product expands.",
      },
      {
        id: "INV-11",
        q: "What is research or future rather than currently implemented?",
        a: "Broader molecular, spatial, wearable, environmental, and cross-disease capabilities are part of the platform's expansion path. They are intended to build on the same underlying product vision rather than require a separate platform.",
      },
      {
        id: "INV-12",
        q: "What evidence exists today?",
        a: "The platform's architecture, scientific rationale, and development work provide the foundation for evidence generation. Formal performance and clinical evidence will be established through appropriately designed studies and validation programs.",
      },
      {
        id: "INV-13",
        q: "What still needs validation?",
        a: "Key areas for validation include performance, robustness, calibration, generalization across sites and populations, usability, and the value added by multimodal information. These are evaluated for each intended use rather than assumed in advance.",
      },
      {
        id: "INV-14",
        q: "What is proprietary?",
        a: "DI's differentiation is centered on how it organizes heterogeneous disease evidence into a coherent intelligence platform and how that platform can support multiple translational workflows. Specific intellectual-property positions are being developed separately.",
      },
      {
        id: "INV-15",
        q: "What is the IP strategy?",
        a: "The IP strategy focuses on protectable technical and product innovations around the platform. Patent scope is determined through invention documentation, prior-art analysis, and legal review.",
      },
      {
        id: "INV-16",
        q: "How difficult would DI be to replicate?",
        a: "The individual technologies used in DI are part of a broader AI ecosystem. The product challenge is bringing them together into a useful, reliable, disease-oriented workflow with the appropriate data, evaluation, and operational discipline.",
      },
      {
        id: "INV-17",
        q: "How can the architecture scale to additional diseases?",
        a: "DI is being built as an extensible disease platform. Pulmonary tuberculosis is the initial focus, with the longer-term opportunity to apply the approach to additional diseases and organ systems as the relevant data and validation programs are established.",
      },
      {
        id: "INV-18",
        q: "What is the regulatory strategy?",
        a: "The initial positioning is research and translational intelligence. Any clinical use would be assessed according to its intended use, functionality, claims, and the regulatory requirements of the relevant jurisdiction.",
      },
      {
        id: "INV-19",
        q: "What is the business model?",
        a: "Potential commercial models include research collaborations, subscriptions, enterprise arrangements, sponsored programs, and study-based engagements. The model can be adapted to the partner and use case.",
      },
      {
        id: "INV-20",
        q: "How will DI demonstrate measurable incremental value over unimodal systems and conventional ensembles?",
        a: "DI will be evaluated against strong unimodal approaches and conventional multimodal or ensemble alternatives using predefined tasks and meaningful measures. The objective is to demonstrate measurable value, not assume it.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: "hospitals",
    short: "Hospitals",
    title: "Hospitals / Health Systems",
    description:
      "Institutional adoption, governance, integration, deployment, workflow, security, economics, and sustainability.",
    items: [
      {
        id: "HOS-01",
        q: "What specific hospital or health-system problem does DI address?",
        a: "Hospitals often hold relevant disease information across multiple systems. DI is designed to connect that information for research, disease characterization, stratification, biomarker work, and translational programs.",
      },
      {
        id: "HOS-02",
        q: "Which service line or department is the initial adopter?",
        a: "The initial focus is pulmonary and respiratory medicine, with tuberculosis as the first disease focus. Other departments can participate as the platform expands.",
      },
      {
        id: "HOS-03",
        q: "Is the intended use research, clinical decision support, or both?",
        a: "The initial positioning is research and translational intelligence. Clinical decision support is a potential future direction that would require appropriate evidence, governance, and regulatory assessment.",
      },
      {
        id: "HOS-04",
        q: "Who is the institutional accountable owner?",
        a: "Ownership would typically sit with an appropriate clinical, research, informatics, or executive sponsor, depending on the institution. DI is designed to support clear governance rather than create ambiguity about responsibility.",
      },
      {
        id: "HOS-05",
        q: "What governance, ethics, IRB, quality, or other institutional approvals are required?",
        a: "Requirements depend on the project and institution and may include research ethics or IRB review, data-governance approval, privacy review, and other institutional processes. The study plan should define these requirements before data are used.",
      },
      {
        id: "HOS-06",
        q: "What regulatory status is required for the intended use?",
        a: "Regulatory requirements depend on the intended use, functionality, claims, and jurisdiction. Research use and future clinical use should therefore be evaluated separately.",
      },
      {
        id: "HOS-07",
        q: "How does DI integrate with EHR, PACS, LIS, pathology systems, and data warehouses?",
        a: "DI is designed to work with existing healthcare data environments and standard interoperability approaches. Specific interfaces and integrations can be defined for each deployment.",
      },
      {
        id: "HOS-08",
        q: "Does it support relevant interoperability standards such as FHIR, DICOM/DICOMweb, HL7, and IHE?",
        a: "Common healthcare interoperability standards are part of the platform's integration direction. The exact standards and connectors used will depend on the institution and deployment.",
      },
      {
        id: "HOS-09",
        q: "Where are data stored and processed — on-premises, cloud, or hybrid?",
        a: "DI is designed to support on-premises, cloud, and hybrid deployment models. The appropriate choice depends on institutional security, data-governance, infrastructure, and scale requirements.",
      },
      {
        id: "HOS-10",
        q: "How are privacy, security, access control, auditability, and incident response handled?",
        a: "Security and privacy are treated as core deployment requirements, with controls selected for the environment and applicable regulations. The exact implementation is deployment-specific and subject to institutional review.",
      },
      {
        id: "HOS-11",
        q: "What uptime, latency, maintenance, and support requirements exist?",
        a: "Performance, availability, support, and maintenance requirements will be defined for the intended workload. Research environments and production clinical environments may have different operational requirements.",
      },
      {
        id: "HOS-12",
        q: "What is the expected total cost of ownership over several years?",
        a: "Total cost depends on deployment scope, data volume, infrastructure, integration, and support requirements. DI is designed to build on existing data and infrastructure where practical.",
      },
      {
        id: "HOS-13",
        q: "How would the system be procured — subscription, per-study, enterprise license, research funding, or another model?",
        a: "Engagement can be structured as a research collaboration, study-based program, subscription, enterprise arrangement, or another mutually appropriate model.",
      },
      {
        id: "HOS-14",
        q: "Does DI have a potential reimbursement pathway?",
        a: "The initial value proposition is research and translational impact rather than reimbursement for clinical decisions. Any future clinical application would require its own economic and reimbursement assessment.",
      },
      {
        id: "HOS-15",
        q: "What impact could deployment have on radiology, pathology, laboratory, clinical, and IT workflows?",
        a: "DI is intended to complement existing workflows rather than replace core hospital systems. The goal is to use available information with minimal unnecessary disruption.",
      },
      {
        id: "HOS-16",
        q: "How are false positives, false negatives, uncertainty, and conflicting inputs handled?",
        a: "The platform is designed to make uncertainty and conflicting evidence visible rather than hide them. Appropriate evaluation and presentation of uncertainty are important parts of responsible deployment.",
      },
      {
        id: "HOS-17",
        q: "How are responsibility and accountability defined when DI contributes information to a clinical or research process?",
        a: "DI provides information to support research or clinical workflows; responsibility remains with the appropriately authorized professional or institution. Governance should define accountability for each use case.",
      },
      {
        id: "HOS-18",
        q: "How does performance vary across the institution's patient populations, sites, scanners, laboratories, and other sources of variation?",
        a: "Performance across sites, populations, equipment, laboratories, and other sources of variation is an important validation question. DI is intended to be evaluated under those real-world conditions.",
      },
      {
        id: "HOS-19",
        q: "How will model drift, bias, equity, performance, and quality be monitored after deployment?",
        a: "Ongoing monitoring is part of the intended lifecycle, including performance, data quality, drift, and relevant subgroup or site behavior. The specific monitoring plan depends on the deployment.",
      },
      {
        id: "HOS-20",
        q: "What training, change-management, vendor-dependency, continuity, and exit/migration plans are required?",
        a: "Training, change management, continuity, and data portability should be addressed in the deployment plan. DI is intended to work with standard interfaces and avoid unnecessary dependence on a single infrastructure environment.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: "clinicians",
    short: "Clinicians",
    title: "Doctors / Clinicians",
    description:
      "Clinical and research usefulness, evidence, workflow, uncertainty, multimodal context, and professional responsibility.",
    items: [
      {
        id: "CLN-01",
        q: "What specific clinical or research problem does DI address?",
        a: "DI is designed to help bring fragmented disease evidence together so clinicians and researchers can study the disease more comprehensively. The initial focus is translational research in pulmonary tuberculosis.",
      },
      {
        id: "CLN-02",
        q: "What does the clinician actually see?",
        a: "The experience is intended to present understandable disease-related insights, supporting evidence, and relevant multimodal context. It is designed as an intelligence workspace rather than a black-box replacement for professional judgment.",
      },
      {
        id: "CLN-03",
        q: "Does DI assist clinicians rather than replace clinical judgment?",
        a: "Yes. The platform is intended to support human decision-making and research rather than replace professional judgment.",
      },
      {
        id: "CLN-04",
        q: "What evidence supports its intended use?",
        a: "Evidence will be built through structured technical, scientific, and—where applicable—clinical validation. Claims will be tied to the evidence generated for the specific intended use.",
      },
      {
        id: "CLN-05",
        q: "What happens when one or more modalities are missing?",
        a: "The platform is designed to work with available modalities and to recognize when information is missing. The significance of missing information is evaluated rather than automatically treating absence as negative evidence.",
      },
      {
        id: "CLN-06",
        q: "What happens when modalities provide conflicting signals?",
        a: "DI is designed to surface disagreement between evidence sources. This allows the user to see that the signals differ rather than receiving an artificially simplified conclusion.",
      },
      {
        id: "CLN-07",
        q: "What are the expected false-positive and false-negative implications?",
        a: "False-positive and false-negative behavior is a validation question for each intended use. Appropriate performance, calibration, and uncertainty analyses will be used to characterize it.",
      },
      {
        id: "CLN-08",
        q: "How accurate and calibrated is the system?",
        a: "Accuracy and calibration are measured as part of validation rather than assumed. Results will be reported for the specific task, dataset, population, and validation setting.",
      },
      {
        id: "CLN-09",
        q: "Which patient populations have been evaluated?",
        a: "The initial scientific focus is pulmonary tuberculosis. Broader populations and diseases will require their own appropriate data and validation programs.",
      },
      {
        id: "CLN-10",
        q: "How does performance vary across hospitals and sites?",
        a: "Site-to-site performance is an important part of validation. The platform is intended to be evaluated across relevant institutions and populations rather than relying only on a single-site result.",
      },
      {
        id: "CLN-11",
        q: "How robust is DI to different scanners, laboratories, and acquisition conditions?",
        a: "Robustness to differences in scanners, laboratories, acquisition conditions, and other sources of variation is an explicit validation goal. Performance should be demonstrated under representative conditions.",
      },
      {
        id: "CLN-12",
        q: "What evidence is shown to support an output?",
        a: "The intended experience is evidence-linked: users should be able to understand what information contributed to an output and how confident or uncertain the result is.",
      },
      {
        id: "CLN-13",
        q: "How does DI handle poor-quality or incomplete data?",
        a: "The platform is designed to recognize incomplete or lower-quality information and reflect that in the interpretation. Poor-quality inputs should not silently create unwarranted confidence.",
      },
      {
        id: "CLN-14",
        q: "How can DI help characterize disease phenotype?",
        a: "DI can support phenotype characterization by relating multiple forms of disease evidence within a common framework. This can help identify patterns and patient subgroups for research.",
      },
      {
        id: "CLN-15",
        q: "Can it support longitudinal analysis?",
        a: "Longitudinal analysis is an intended capability for studying how disease-related evidence changes over time. Its usefulness depends on the availability and quality of longitudinal data.",
      },
      {
        id: "CLN-16",
        q: "How does it fit into PACS, EHR, pathology, or other existing workflows?",
        a: "DI is intended to complement existing clinical and research systems rather than replace them. Integration can be designed around the data environments already used by the organization.",
      },
      {
        id: "CLN-17",
        q: "How much additional work does it create for clinicians?",
        a: "The goal is to minimize additional burden by making use of information that is already available. The actual workflow impact must be evaluated in the target environment.",
      },
      {
        id: "CLN-18",
        q: "What is its regulatory status for the intended use?",
        a: "The initial positioning is research and translational intelligence. Any clinical application would require assessment of the appropriate regulatory pathway for its intended use and jurisdiction.",
      },
      {
        id: "CLN-19",
        q: "Who remains responsible for clinical decisions?",
        a: "Clinical responsibility remains with the appropriately qualified clinician. DI is intended to provide information and supporting evidence, not to assume clinical accountability.",
      },
      {
        id: "CLN-20",
        q: "What prospective validation is planned or required?",
        a: "Clinical validation would be part of the evidence program for any intended clinical use. The design, endpoints, population, and setting would be defined for that specific use case.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: "scientists",
    short: "Scientists",
    title: "Scientists",
    description:
      "Scientific rationale, multimodal evidence, data quality, validation, generalization, and biological interpretation.",
    items: [
      {
        id: "SCI-01",
        q: "What exactly is the shared representation?",
        a: "The platform is designed around a shared disease-oriented representation that allows information from different modalities to be related. The representation is learned and evaluated for its usefulness to the intended scientific tasks.",
      },
      {
        id: "SCI-02",
        q: "How are different modalities aligned?",
        a: "Different modalities are brought into a common disease-oriented framework through modality-aware representation and alignment. The goal is to preserve useful modality-specific information while making meaningful relationships possible.",
      },
      {
        // PATENT-REVIEWED: question and answer generalized; no mention of projection mechanics.
        id: "SCI-03",
        q: "How is each modality interpreted before it is related to the others?",
        a: "Each modality is first interpreted in a form appropriate to that data type. The platform then relates those interpretations within a common disease-oriented framework.",
      },
      {
        // PATENT-REVIEWED: dimension and "compact" wording removed.
        id: "SCI-04",
        q: "How was the shared representation configured and validated?",
        a: "The current design uses a shared representation as a common interface across modalities. Its configuration is ultimately an empirical question and will be evaluated across relevant tasks and datasets.",
      },
      {
        // PATENT-REVIEWED: specific dimension removed from the question and answer.
        id: "SCI-05",
        q: "Is the shared representation a fixed requirement or an empirical design choice?",
        a: "The shared representation is a design choice, not a biological claim about the structure of disease. Its value must be demonstrated through scientific and downstream evaluation.",
      },
      {
        id: "SCI-06",
        q: "How are native modality representation sizes handled?",
        a: "Native representations can differ substantially in size and structure. DI is designed to accommodate those differences rather than forcing every modality into the same native representation.",
      },
      {
        // PATENT-REVIEWED: three-tier pairing taxonomy reduced to paired / unpaired.
        id: "SCI-07",
        q: "Can the system train with unpaired as well as paired data?",
        a: "Yes. The platform is designed to make use of paired and unpaired data where appropriate. The role of each data type depends on the learning and evaluation objective, and paired or linked data are especially important for certain benchmarks and validation studies.",
      },
      {
        id: "SCI-08",
        q: "How is missingness represented and evaluated?",
        a: "Missingness is treated as meaningful information about what is and is not available for a given observation. The platform is designed to evaluate performance under realistic patterns of missing data.",
      },
      {
        id: "SCI-09",
        q: "How is modality dominance controlled?",
        a: "The platform is designed to prevent one modality from overwhelming the contribution of others. This is evaluated through balancing, ablation, and modality-specific analysis.",
      },
      {
        id: "SCI-10",
        q: "How are scanner, stain, acoustic recording, site, and other technical effects addressed?",
        a: "Technical variation such as site, scanner, stain, and recording conditions is treated as an important source of variation. Evaluation is designed to test whether useful disease-related signals remain robust across those conditions.",
      },
      {
        id: "SCI-11",
        q: "How are biological batch effects handled?",
        a: "Biological and technical batch effects require explicit study and appropriate normalization or modeling. The platform's validation program is designed to distinguish biological signal from unwanted technical variation.",
      },
      {
        id: "SCI-12",
        q: "How are patient, site, and temporal data splits constructed?",
        a: "Data splitting should respect patient, site, and time boundaries where relevant. This helps prevent leakage and provides a more realistic assessment of generalization.",
      },
      {
        id: "SCI-13",
        q: "What are the unimodal baselines?",
        a: "Each modality should have a strong standalone baseline appropriate to the task. Multimodal results are then compared with those baselines using the same evaluation framework.",
      },
      {
        id: "SCI-14",
        q: "What are the multimodal ablations?",
        a: "Ablation studies remove or vary components and modalities to determine where the observed value comes from. This is important for showing whether multimodal integration adds something beyond individual inputs.",
      },
      {
        id: "SCI-15",
        q: "How is uncertainty quantified?",
        a: "Uncertainty is evaluated using appropriate confidence and calibration methods for the task. The goal is to distinguish strong evidence from cases where the system should be less certain.",
      },
      {
        id: "SCI-16",
        q: "How is calibration evaluated?",
        a: "Calibration is evaluated with task-appropriate methods such as reliability analysis and calibration metrics. Results should be reported alongside performance rather than treated as an afterthought.",
      },
      {
        id: "SCI-17",
        q: "How is biological concordance assessed?",
        a: "Biological concordance can be examined against established scientific knowledge, independent evidence, and relevant outcomes. The purpose is to determine whether learned relationships have meaningful biological interpretation.",
      },
      {
        id: "SCI-18",
        q: "Can relationships be evaluated through cross-modal retrieval or translation?",
        a: "Yes. Cross-modal retrieval and related tasks can test whether the platform captures meaningful relationships between different types of disease evidence. Translation-oriented tasks can be used where they are scientifically appropriate.",
      },
      {
        id: "SCI-19",
        q: "How does DI compare with unimodal systems and output-level ensembles?",
        a: "The comparison is made using the same tasks and evaluation data for strong unimodal systems, conventional ensembles, and the multimodal approach. The question is whether the added integration produces measurable value.",
      },
      {
        id: "SCI-20",
        q: "How is external-site generalization demonstrated?",
        a: "External-site evaluation is used to test whether findings transfer beyond the data used for development. Site-stratified and cohort-specific results help reveal where generalization is strong or limited.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: "ai-researchers",
    short: "AI researchers",
    title: "AI Researchers",
    description:
      "Multimodal learning, representation, evaluation, baselines, ablations, generalization, and reproducibility.",
    items: [
      {
        id: "AIR-01",
        q: "Why is multimodal learning necessary for the intended problem?",
        a: "Disease information is distributed across multiple data types, and some useful relationships may only appear when those data are considered together. DI is designed to test whether multimodal integration adds measurable value over strong individual models and ensembles.",
      },
      {
        id: "AIR-02",
        q: "What is the optimization objective?",
        a: "The learning objectives are designed to encourage useful cross-modal relationships, disease relevance, and robust representations. Exact objectives can evolve with the task and implementation.",
      },
      {
        id: "AIR-03",
        q: "How are modality-specific representations constructed?",
        a: "Each modality is handled according to its own characteristics before being related to the broader disease representation. This allows the platform to work across fundamentally different data types.",
      },
      {
        id: "AIR-04",
        q: "How are modalities aligned?",
        a: "DI aligns information from different modalities within a common disease-oriented framework. The objective is meaningful semantic correspondence rather than simple numerical concatenation.",
      },
      {
        id: "AIR-05",
        q: "What is the role of the shared semantic space?",
        a: "The shared representation provides a common context in which heterogeneous disease evidence can be compared and related. It is intended to support characterization, stratification, discovery, and other task-specific analyses.",
      },
      {
        // PATENT-REVIEWED: dimension and "compact" wording removed.
        id: "AIR-06",
        q: "How is the shared representation configured and validated?",
        a: "The current design uses a shared representation as a common interface. Its configuration remains subject to empirical evaluation rather than being treated as inherently optimal.",
      },
      {
        id: "AIR-07",
        q: "How is representation tunability achieved?",
        a: "Representation tuning comes from adapting the platform to the disease concepts, data types, and task requirements being studied. The aim is to make the learned representation useful and scientifically interpretable.",
      },
      {
        id: "AIR-08",
        q: "How are different native embedding dimensions handled?",
        a: "Different modalities can retain their own native representations before being related within the shared framework. This avoids assuming that all data types have the same underlying structure.",
      },
      {
        // PATENT-REVIEWED: three-tier pairing taxonomy reduced to paired / unpaired.
        id: "AIR-09",
        q: "How are paired and unpaired data used?",
        a: "The platform is designed to use paired and unpaired data where appropriate. Paired or patient-linked data are particularly valuable for certain evaluation and validation tasks, while unpaired data can contribute to broader population-level learning.",
      },
      {
        id: "AIR-10",
        q: "How are missing modalities handled?",
        a: "The platform is designed to operate when some modalities are unavailable and to make that availability explicit. Performance under missing-modality conditions is an important evaluation dimension.",
      },
      {
        id: "AIR-11",
        q: "How is modality dominance detected and mitigated?",
        a: "Modality contribution can be studied through balancing, ablation, and comparative analysis. The objective is to ensure that apparent multimodal value is not simply driven by one dominant input.",
      },
      {
        id: "AIR-12",
        q: "How are dataset, site, scanner, stain, and recording biases addressed?",
        a: "Bias from datasets, sites, equipment, laboratories, and acquisition conditions is addressed through study design, stratified evaluation, data-quality analysis, and appropriate modeling choices.",
      },
      {
        id: "AIR-13",
        q: "How are batch effects handled?",
        a: "Batch effects are treated as a scientific and engineering concern that must be separated from disease signal. Appropriate normalization, modeling, and validation strategies are selected for each data domain.",
      },
      {
        id: "AIR-14",
        q: "How are conflicts between modalities represented?",
        a: "The platform is designed to preserve and communicate disagreement between modalities when it is relevant. This supports more transparent interpretation than forcing all signals into a single undifferentiated answer.",
      },
      {
        id: "AIR-15",
        q: "What cross-modal evaluation tasks are used?",
        a: "Evaluation can include cross-modal retrieval, representation alignment, translation-oriented tasks, and downstream research or clinical tasks appropriate to the intended use.",
      },
      {
        id: "AIR-16",
        q: "What unimodal, multimodal, and ensemble baselines are compared?",
        a: "The evaluation framework includes strong unimodal baselines, conventional output-level ensembles, and the multimodal approach. Comparisons use common datasets, tasks, and predefined measures wherever possible.",
      },
      {
        id: "AIR-17",
        q: "What ablation studies isolate the contribution of each component?",
        a: "Ablations can examine individual modalities, integration choices, alignment mechanisms, and missing-data conditions. These experiments help determine which components actually contribute to observed performance.",
      },
      {
        id: "AIR-18",
        q: "How is generalization tested?",
        a: "Generalization is tested using held-out cohorts, sites, and—where appropriate—temporal splits. The goal is to understand how well the approach transfers beyond the development data.",
      },
      {
        id: "AIR-19",
        q: "What evidence demonstrates value beyond conventional unimodal and ensemble approaches?",
        a: "The value proposition is tested empirically through predefined comparisons and ablations. DI should earn any claimed incremental value through evidence rather than through the number of modalities included.",
      },
      {
        id: "AIR-20",
        q: "How are primary endpoints, statistical uncertainty, and reproducibility pre-specified for the multimodal comparison?",
        a: "A rigorous evaluation plan should define primary endpoints, statistical methods, uncertainty, data splits, and reproducibility requirements before the comparison is run. This makes the result more credible and easier to reproduce.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: "pharma",
    short: "Pharma & biotech",
    title: "Pharma, Biotech & Therapeutics",
    description:
      "Translational research, biomarkers, stratification, treatment-response research, partnerships, and development programs.",
    items: [
      {
        id: "PHM-01",
        q: "What problem does DI solve for pharma and biotech?",
        a: "Pharma and biotech organizations generate rich data across clinical, imaging, pathology, molecular, and other domains. DI is designed to connect these sources to support disease characterization, biomarker research, patient stratification, and translational investigation.",
      },
      {
        id: "PHM-02",
        q: "Where does DI fit in the drug-development lifecycle?",
        a: "DI is positioned primarily as a translational and research platform. Potential applications include biomarker strategy, patient stratification, mechanism investigation, and evidence generation supporting development programs.",
      },
      {
        id: "PHM-03",
        q: "What is the initial therapeutic area?",
        a: "The initial focus is pulmonary tuberculosis. The broader platform is intended to extend to additional diseases as relevant data, partnerships, and validation programs are established.",
      },
      {
        id: "PHM-04",
        q: "How could DI support biomarker discovery?",
        a: "DI can support biomarker hypothesis generation by relating multimodal disease patterns to relevant outcomes and biological concepts. Candidate biomarkers would still require appropriate independent validation.",
      },
      {
        id: "PHM-05",
        q: "How could DI support patient stratification for trials?",
        a: "DI is designed to support research into patient subgroups with distinct multimodal characteristics. Whether those groups predict treatment response is an empirical question requiring appropriate validation.",
      },
      {
        id: "PHM-06",
        q: "How does DI handle molecular and omics data?",
        a: "Molecular and omics data are part of the platform's expansion path. DI is designed to accommodate additional biological data types alongside clinical, imaging, pathology, and acoustic evidence.",
      },
      {
        id: "PHM-07",
        q: "Can DI support pharmacogenomic and pharmaco-epigenomic research?",
        a: "Pharmacogenomic and epigenetic research are potential extensions of the platform. Such applications would require appropriate data, study design, and validation.",
      },
      {
        id: "PHM-08",
        q: "How does DI handle missing modalities in real-world trial data?",
        a: "DI is designed to work with incomplete multimodal datasets and to recognize which information is available. The effect of missing data should be evaluated for each study rather than assumed to be harmless.",
      },
      {
        // PATENT-REVIEWED: "semi-paired" replaced with plain-language "partially linked".
        id: "PHM-09",
        q: "How does DI handle unpaired and partially linked trial data?",
        a: "The platform can make use of paired, partially linked, and unpaired data where appropriate. Linked data are particularly important for certain benchmarking and validation questions, while unpaired data can contribute to broader research and representation learning.",
      },
      {
        id: "PHM-10",
        q: "How would DI compare to existing multimodal or ensemble approaches in a pharma setting?",
        a: "DI will be compared with strong unimodal and ensemble approaches using predefined research tasks and measures. The objective is to determine whether its integrated disease representation provides measurable additional value.",
      },
      {
        id: "PHM-11",
        q: "Can DI be used to investigate mechanism of action or resistance?",
        a: "DI can support investigation of relationships between multimodal disease patterns and treatment-related outcomes. It is intended to generate research hypotheses, not to claim mechanistic proof automatically.",
      },
      {
        id: "PHM-12",
        q: "Can DI be used with retrospective trial or real-world data?",
        a: "Yes, retrospective trial and real-world data can be valuable inputs where access, governance, data quality, and appropriate approvals are in place. Prospective studies may also be needed for particular validation objectives.",
      },
      {
        id: "PHM-13",
        q: "How does DI handle site, scanner, stain, and batch effects?",
        a: "Site, scanner, stain, batch, and other sources of variation are important validation considerations. DI is intended to be evaluated across these conditions to understand robustness and limitations.",
      },
      {
        id: "PHM-14",
        q: "How is uncertainty represented in DI outputs?",
        a: "The platform is designed to communicate uncertainty alongside results. Appropriate calibration and confidence analysis will depend on the specific research task.",
      },
      {
        id: "PHM-15",
        q: "How does DI handle conflicting signals across modalities?",
        a: "DI is designed to preserve meaningful disagreement between evidence sources rather than hide it. This can help researchers investigate why different data types point in different directions.",
      },
      {
        id: "PHM-16",
        q: "What is the regulatory posture for use in pharma R&D?",
        a: "For pharma R&D, DI is positioned as a research and translational intelligence platform. Regulatory requirements for a particular use depend on intended use, functionality, claims, and jurisdiction.",
      },
      {
        id: "PHM-17",
        q: "What data-governance and security model is intended?",
        a: "Data governance and security are designed to reflect the sensitivity of healthcare and research data. Specific controls, responsibilities, and compliance requirements are defined for each deployment.",
      },
      {
        id: "PHM-18",
        q: "How does DI avoid vendor lock-in for pharma partners?",
        a: "DI is designed around interoperable data practices and flexible deployment. The objective is to let partners work with their existing data environments rather than create unnecessary dependence on a single system.",
      },
      {
        id: "PHM-19",
        q: "What is the commercial engagement model for pharma and biotech?",
        a: "Potential engagements include research collaborations, sponsored programs, biomarker or stratification studies, platform licensing, and other partnership structures. The appropriate model depends on the partner and research objective.",
      },
      {
        id: "PHM-20",
        q: "What would a first pilot with a pharma or biotech partner look like?",
        a: "A first pilot would typically address one well-defined translational question with agreed data, scope, governance, and success criteria. The goal is to generate a focused body of evidence that can guide the next stage of collaboration.",
      },
    ],
  },
];

export const faqClosing = {
  title: "Closing perspective",
  paragraphs: [
    "Diagnostic Intelligence is being developed as a platform rather than a single-purpose model. Its long-term value is intended to come from bringing diverse disease evidence into a coherent product environment that can support multiple research, translational, and—where appropriately validated—clinical applications.",
    "The platform vision is deliberately ambitious: start with a focused, meaningful use case; build the product around real-world data and workflows; demonstrate value through rigorous evaluation; and expand to additional diseases, modalities, partners, and therapeutic applications over time.",
  ],
  rows: [
    {
      term: "Diagnostic Intelligence",
      text: "Multimodal intelligence for disease understanding, translational research, and precision medicine.",
    },
    {
      term: "Initial focus",
      text: "Pulmonary tuberculosis and related translational research.",
    },
    {
      term: "Long-term direction",
      text: "An extensible platform connecting diverse forms of disease evidence across research and healthcare.",
    },
  ],
};

export const faqTotal = faqParts.reduce((n, p) => n + p.items.length, 0);
